import React, { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate } from "react-router-dom";

import { useTranslation } from "core/hooks/useTranslation";
import usePermissions from 'core/hooks/usePermission';


import SubTabTableDashboardLayout from "components/layouts/tab/subtab/table";
import Table from 'components/common/table';
import Icon from "components/common/icon";
import AddEditDomain from "./domain-add-edit.component";

import useUnsavedChangesConfirmation from 'hooks/useUnsavedChangesConfirmation';

import { tableColumnsCreator } from 'utils/tableColumnsCreator';
import { deleteQueryStringParamsToUrl } from 'utils/queryString';

import { PERMISSION_RESOURCE, PERMISSION_ACTION } from 'core/constants/permission';
import { UNSAVED_FORM_PAGE_TYPE } from 'constants/common.constants';
import { getTableColumns } from './columns';

import useDomains from './useDomains';
import brandService from 'core/services/apiServices/brandService';

const ROW_UNIQUE_KEY_PROP = "id";

const BrandDomains = () => {
    const { t } = useTranslation();

    const permissionUtils = usePermissions();

    const { search, hash, pathname } = useLocation();
    const navigate = useNavigate();

    const [ isSaving , setIsSaving ] = useState(false)

    const [addEditPopupData, setAddEditPopupData] = useState({
        isVisible: false,
        domain: null
    });

    const navigateWithConfirmation = useUnsavedChangesConfirmation({
        cb: navigate,
        subscribe: [UNSAVED_FORM_PAGE_TYPE.SUB_TAB]
    })

    const brandId = (new URLSearchParams(search)).get("brandId");

    const hasModifyPermission = permissionUtils.has( PERMISSION_RESOURCE.PROJECT_BRANDS, PERMISSION_ACTION.MODIFY);

    const {
        loading,
        domains,
        addDomain,
        removeDomain,
        updateDomain,
        setDomainAsDefault
    } = useDomains(brandId);

    console.log({domains})

    const onDomainSave = useCallback((domain, isEditMode) => {
        if(isEditMode){
            updateDomain(domain)
        } else {
            addDomain(domain)
        }
    }, [updateDomain, addDomain])

    const handleDomainDelete = domain => {
        setIsSaving(true)
        brandService.deleteDomain({
            id: domain.id
        })
            .then(() => removeDomain(domain.id))
            .finally(() => setIsSaving(false))
    }

    const handleDomainSetDefault = domain => {
        setIsSaving(true)
        brandService.setDomainAsDefault({
            id: domain.id
        })
            .then(() => setDomainAsDefault(domain.id))
            .finally(() => setIsSaving(false))
    }

    const showAddEditPopup = domain => {
        setAddEditPopupData({
            isVisible: true,
            domain
        })
    }

    const hideAddEditPopup = useCallback(() => {
        setAddEditPopupData({
            isVisible: false,
            domain: null
        })
    }, [setAddEditPopupData])


    const { mainTableColumns } = useMemo(() => {
        return tableColumnsCreator({
            mainColumns: getTableColumns,
            additionalProps: {t},
            t
        });
    }, [t])

    const tableRowActions = [];

    
    if (hasModifyPermission) {
        tableRowActions.push({
            title: t("backoffice.common.edit"),
            icon: "edit",
            onClick: domain => showAddEditPopup(domain),
        })

        tableRowActions.push({
            title: t("backoffice.common.setDefault"),
            icon: "default",
            onClick: handleDomainSetDefault,
            disabled: record => record.isDefault
        })

        tableRowActions.push({
            title: t('backoffice.common.delete'),
            confirmationMessage: t("backoffice.projects.doYouWanttoDeleteBrandDomain"),
            confirmationTitle: t("backoffice.projects.deleteBrandDomain"),
            icon: "trash",
            showConfirmation: true,
            onClick: handleDomainDelete,
            disabled: record => record.isUsed || record.isDefault
        })
    }

    const headerPartsData = {
        button: {
            icon: "plus",
            type: "primary",
            onClick: () => showAddEditPopup(null),
            text: t("backoffice.projects.addDomain"),
            enabled: hasModifyPermission
        }
    }


    return (
        <SubTabTableDashboardLayout
            header={headerPartsData}
            buttons={[
                {
                    type: "secondary",
                    onClick: () => {
                        const url = deleteQueryStringParamsToUrl(pathname, search, hash, ["brandId", "brandName"] );
                        navigateWithConfirmation(url, { replace: true } )
                    },
                    text: (
                        <span className='rt--back-button'>
                            <Icon name="left" size={16} />
                            <span>
                                {t("backoffice.common.back")}
                            </span>
                        </span>
                    ),
                    enabled: true,
                    className: "rt--button-secondary",
                    placement: "left",
                },
            ]}
        >
            <Table
                loading={loading || isSaving}
                columns={mainTableColumns}
                data={domains}
                uniqueKey={ROW_UNIQUE_KEY_PROP}
                disableFullView={true}
                actions={tableRowActions}
                noPagination={true}
            />

            <AddEditDomain
                isVisible={addEditPopupData.isVisible}
                domain={addEditPopupData.domain}
                onSave={onDomainSave}
                onClose={hideAddEditPopup}
            />
        </SubTabTableDashboardLayout>
    );
}

export default BrandDomains;